// 全局错误边界：页面渲染抛错（组件 bug / 数据形态异常）时展示整页错误页 + 刷新按钮，
// 替代默认白屏。渲染崩溃时页面上下文不可信，故不走 toast。
// class 组件无法使用 useTranslation hook，直接经 i18next 实例取当前语言文案。
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 控制台保留原始堆栈供排查（渲染崩溃时无可靠上报通道）
    console.error('[ErrorBoundary] 页面渲染异常', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h1 className="mt-4 text-base font-semibold text-slate-900">
              {i18n.t('errors.boundary.title')}
            </h1>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              {i18n.t('errors.boundary.description')}
            </p>
            <p className="mt-3 text-xs text-slate-400 break-all bg-slate-50 rounded-md p-2 text-left font-mono">
              {this.state.error.message || String(this.state.error)}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 w-full inline-flex justify-center items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              {i18n.t('errors.boundary.reload')}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
